import { useState, useMemo, useRef, useEffect } from 'react';
import Popover from './Popover';

/**
 * QHS InlinePicker (Tier 2)
 * -------------------------
 * Single-select dropdown. Click value → applied → closes. No Apply button.
 * Auto search when options > 8.
 *
 *   <InlinePicker
 *     open={open} onClose={close}
 *     trigger={<Button>...</Button>}
 *     value={current}
 *     options={[{ value, label, tag? }, ...]}
 *     onChange={v => ...}
 *     searchThreshold={8}
 *   />
 *
 * Group support
 * -------------
 * If an option carries its own `options: [...]` array (e.g. clinical
 * exam findings split into Nasal / Eye / Dermatological / ...), the
 * picker renders the parent as a non-selectable section header and
 * the children as indented selectable rows. This is purely additive
 * — flat option arrays keep working unchanged.
 *
 *   options=[
 *     { value: 'nasal', label: 'Nasal & Sinus', options: [
 *       { value: 'rhinorrhea', label: 'Rhinorrhea' }, ...
 *     ] },
 *     { value: 'eye', label: 'Eye', options: [...] },
 *   ]
 *
 * Search filters at the leaf level. A group with no matching leaves
 * after filtering is hidden so the list doesn't show empty headers.
 */

const isGroup = (o) => o && Array.isArray(o.options);

const InlinePicker = ({
  open,
  onClose,
  trigger,
  value,
  options = [],
  onChange,
  placement = 'bottom-start',
  searchThreshold = 8,
  searchPlaceholder = 'Search...',
  emptyText = 'No options found',
  multiple = false,
  // Server-side search: always show the box, report each query, and
  // leave filtering to the caller (it passes the matching `options`).
  onSearch,
  ...rest
}) => {
  const [q, setQ] = useState('');
  const searchRef = useRef(null);

  // Total leaf count drives the search-input threshold (so a 5-group
  // 30-leaf list shows search; a flat 5-leaf list does not).
  const leafCount = useMemo(
    () =>
      options.reduce((acc, o) => acc + (isGroup(o) ? o.options.length : 1), 0),
    [options]
  );
  const showSearch = !!onSearch || leafCount > searchThreshold;

  // Reset the query each time the picker closes so a reopen starts fresh —
  // the caller's too, when it searches server-side. A ref keeps an inline
  // onSearch from re-running this on every render.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  useEffect(() => {
    if (open) return;
    setQ('');
    onSearchRef.current?.('');
  }, [open]);

  // Focus the search box when the picker opens. The `autoFocus` attribute
  // is unreliable here: Popover mounts its content with `visibility:hidden`
  // until it measures its position, and a hidden element cannot take focus,
  // so the attribute fires too early and is dropped — leaving the operator
  // unable to type until they click the box. Re-focus on the next frame,
  // once the popover has been made visible.
  useEffect(() => {
    if (!open || !showSearch) return undefined;
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, showSearch]);

  const filtered = useMemo(() => {
    if (!q || onSearch) return options;
    const needle = q.toLowerCase();
    const matches = (o) => String(o.label ?? o).toLowerCase().includes(needle);
    return options
      .map((o) => {
        if (!isGroup(o)) return matches(o) ? o : null;
        const subs = o.options.filter(matches);
        // also keep the group if its own header text matches
        if (subs.length === 0 && !matches(o)) return null;
        return { ...o, options: subs.length > 0 ? subs : o.options };
      })
      .filter(Boolean);
  }, [options, q, onSearch]);

  const asArray = Array.isArray(value) ? value : [];
  const isChecked = (val) => (multiple ? asArray.includes(val) : val === value);

  const handlePick = (val, o) => {
    if (multiple) {
      const next = asArray.includes(val)
        ? asArray.filter((v) => v !== val)
        : [...asArray, val];
      onChange?.(next, o);
      return;
    }
    onChange?.(val, o);
    onClose?.();
  };

  const renderItem = (o, i, opts = {}) => {
    const val = o.value ?? o;
    const isActive = isChecked(val);
    return (
      <button
        key={o.key ?? val ?? i}
        type='button'
        className={`qhs-pop__item${isActive ? (multiple ? ' is-checked' : ' is-active') : ''}${
          opts.indented ? ' qhs-pop__item--indented' : ''
        }`}
        disabled={!!o.disabled}
        onClick={() => handlePick(val, o)}
      >
        {multiple && (
          <span className={`qhs-pop__item-ctrl${isActive ? ' is-checked' : ''}`} aria-hidden='true'>
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
              <polyline points='20 6 9 17 4 12' />
            </svg>
          </span>
        )}
        {o.icon && <span className='qhs-pop__item-icon'>{o.icon}</span>}
        <span className='qhs-pop__item-label'>{o.label ?? o}</span>
        {/* Right-aligned annotation ("Default", "Inactive", ...). Text
            only, and deliberately outside the searchable label so it
            cannot match a typed query. */}
        {o.tag && <span className='qhs-pop__item-tag'>{o.tag}</span>}
        {!multiple && isActive && (
          <span className='qhs-pop__item-check'>
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
              <polyline points='20 6 9 17 4 12' />
            </svg>
          </span>
        )}
      </button>
    );
  };

  return (
    <Popover open={open} onClose={onClose} trigger={trigger} placement={placement} {...rest}>
      {showSearch && (
        <div style={{ padding: '8px 8px 0' }}>
          <div className='qhs-pop__search'>
            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <circle cx='11' cy='11' r='8' />
              <path d='m21 21-4.3-4.3' />
            </svg>
            <input
              ref={searchRef}
              autoFocus
              placeholder={searchPlaceholder}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                onSearch?.(e.target.value);
              }}
              onKeyDown={(e) => {
                // Enter selects the first match so the operator can
                // type a few letters and commit without reaching for
                // the mouse (e.g. "flor" + Enter -> Florida).
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // A server-side list is not a local match for the query
                  // (it may still be loading), so never auto-pick from it.
                  if (onSearch) return;
                  const firstLeaf = filtered
                    .flatMap((o) => (isGroup(o) ? o.options : [o]))
                    .find((o) => !o.disabled);
                  if (firstLeaf) handlePick(firstLeaf.value ?? firstLeaf, firstLeaf);
                }
              }}
            />
          </div>
        </div>
      )}
      <div className='qhs-pop__body'>
        {filtered.length === 0 && (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: 'var(--qhs-text-3)',
              fontSize: 12,
            }}
          >
            {emptyText}
          </div>
        )}
        {filtered.map((o, i) => {
          if (isGroup(o)) {
            return (
              <div key={o.key ?? o.value ?? `g-${i}`} className='qhs-pop__group'>
                <div className='qhs-pop__group-header'>{o.label ?? ''}</div>
                {o.options.map((child, j) => renderItem(child, j, { indented: true }))}
              </div>
            );
          }
          return renderItem(o, i);
        })}
      </div>
      <style>{`
        .qhs-pop__search { position: relative; display: flex; align-items: center; }
        .qhs-pop__search svg {
          position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
          width: 14px; height: 14px; color: var(--qhs-text-3); pointer-events: none;
        }
        .qhs-pop__search input {
          width: 100%; padding: 7px 10px 7px 30px; font-size: 13px; font-family: inherit;
          background: var(--qhs-bg); border: 1px solid transparent; border-radius: var(--qhs-r-sm);
          outline: none; color: var(--qhs-text-1);
        }
        .qhs-pop__search input:focus { border-color: var(--qhs-primary); background: var(--qhs-surface); }

        .qhs-pop__group + .qhs-pop__group { margin-top: 4px; }
        .qhs-pop__group-header {
          padding: 8px 12px 4px;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: var(--qhs-text-3);
        }
        .qhs-pop__item--indented { padding-left: 24px; }
      `}</style>
    </Popover>
  );
};

export default InlinePicker;
